import useSWR, { invalidateStartsWith } from 'hooks/useSWR';
import usePaginatedResource from 'util/resource/usePaginatedResource';
import {
  CompanyCompanyConversation,
  CompanyConversation,
  conversationApiUrls,
} from 'apis/CompanyAPI/conversations/conversationApi';
import { PaginatedResult, PaginationOptions } from 'types/api';
import { useSWRResource } from 'util/resource/useSWRResource';

export type InboxConversation = CompanyConversation | CompanyCompanyConversation;

export const PAGE_SIZE = 20;

export const reloadCompanyInbox = (companyId: number) => {
  invalidateStartsWith([conversationApiUrls.company.list(companyId)]);
};

export function useCompanyInbox(
  companyId: number | undefined,
  paginationOptions: PaginationOptions,
  canReadCompanyInbox: boolean,
) {
  const swrResult = useSWR<PaginatedResult<InboxConversation>>(
    canReadCompanyInbox && companyId ? conversationApiUrls.company.list(companyId, paginationOptions) : undefined,
  );

  return useSWRResource(swrResult);
}

/**
 * Accumulating variant used by the inbox list, so paging appends into one continuous list instead
 * of mounting a second page component with its own fetch.
 *
 * `reloadCompanyInbox` still reaches this: usePaginatedResource keys SWR on the page-1 url, and
 * invalidateStartsWith prefix-matches plain string keys as well as array keys.
 */
export function useCompanyInboxPaginated(companyId: number | undefined, canReadCompanyInbox: boolean) {
  return usePaginatedResource<InboxConversation>(
    paginationOptions =>
      canReadCompanyInbox && companyId ? conversationApiUrls.company.list(companyId, paginationOptions) : '',
    { page: 1, limit: PAGE_SIZE },
    undefined,
    { mode: 'append', uniqueSelector: conversation => conversation.id },
  );
}
